import React, { useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';

import '../styles/expandable.css';

export interface ExpandableProps {
  expandable: boolean;
  isExpanded: boolean;
  onToggle: (isExpanded: boolean) => void;
  children: React.ReactNode;
}

export const Expandable: React.FC<ExpandableProps> = ({
  expandable,
  isExpanded,
  onToggle,
  children,
}) => {
  const handleClose = useCallback(() => {
    onToggle(false);
  }, [onToggle]);

  // Escape key closes the dialog
  useEffect(() => {
    if (!isExpanded) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isExpanded, handleClose]);

  // Body scroll lock when expanded
  useEffect(() => {
    if (!isExpanded) return;

    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isExpanded]);

  if (!expandable) {
    return <>{children}</>;
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (isExpanded) {
    return createPortal(
      <div className="expandable-overlay" onClick={handleOverlayClick}>
        <div className="expandable-dialog">
          <div className="expandable-header">
            <button
              type="button"
              className="expandable-collapse-btn"
              onClick={handleClose}
              title="Collapse editor"
              aria-label="Collapse editor"
            >
              <svg width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M1 1L5 5M5 5V1.5M5 5H1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                <path d="M13 13L9 9M9 9V12.5M9 9H12.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
          </div>
          <div className="expandable-body">
            {children}
          </div>
        </div>
      </div>,
      document.body
    );
  }

  return <>{children}</>;
};

export default Expandable;
